import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=auth
  const [entitlements, setEntitlements] = useState(null);

  const loadEntitlements = useCallback(async () => {
    try {
      const r = await api.get("/meta/entitlements");
      setEntitlements(r.data);
    } catch {
      setEntitlements(null);
    }
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        await loadEntitlements();
      })
      .catch(() => setUser(false));
  }, [loadEntitlements]);

  const login = async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    setUser(data);
    await loadEntitlements();
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    await loadEntitlements();
    return data;
  };
  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setEntitlements(null);
  };
  const setCurrency = async (currency) => {
    await api.put("/auth/currency", { currency });
    setUser((u) => ({ ...u, currency }));
  };
  const completeOnboarding = async (payload) => {
    const { data } = await api.post("/onboarding/complete", payload);
    setUser(data);
    await loadEntitlements();
    return data;
  };
  const setDevTier = async (tier) => {
    const { data } = await api.put("/admin/dev-tier", { tier });
    setUser((u) => ({ ...u, tier: data.tier }));
    await loadEntitlements();
    return data;
  };

  const setWorkspaces = async (workspaces) => {
    const { data } = await api.put("/workspaces", { workspaces });
    setUser((u) => ({ ...u, workspaces: data.enabled }));
    await loadEntitlements();
    return data;
  };

  const resendVerification = async () => {
    const { data } = await api.post("/auth/resend-verification");
    return data;
  };
  const refreshMe = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const hasModule = (m) => !m || (entitlements?.modules || []).includes(m);

  return (
    <AuthContext.Provider
      value={{ user, setUser, entitlements, login, register, logout, setCurrency,
               completeOnboarding, setDevTier, setWorkspaces, hasModule, loadEntitlements,
               resendVerification, refreshMe }}
    >
      {children}
    </AuthContext.Provider>
  );
}
